import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { fieldEngineerService, customersApiService } from '../../services';
import { useAuth } from '../../contexts/AuthContext';

const DEFAULT_API_TOKEN = 'b312534ef56be2e2c8bce6a8f844dfa493e7c897c8a1ee3364eece831ad76a56';

function FieldEngineer({ tabColors = { bgcolor: 'bg-orangeFieldEngineer', nocolor: 'bg-orange-100', hovercolor: 'hover:bg-orange-200' } }) {
  const { id } = useParams();
  const { user } = useAuth();

  // Check if user has TopGun role
  const isTopGun = user?.role === 'TopGun';

  // State management
  const [apiToken, setApiToken] = useState(DEFAULT_API_TOKEN);
  const [tokenCopyNotification, setTokenCopyNotification] = useState('');
  const [fieldEngineers, setFieldEngineers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentEngineerIndex, setCurrentEngineerIndex] = useState(0);
  const [selectedEngineer, setSelectedEngineer] = useState(null);
  const [showFullCard, setShowFullCard] = useState(false);
  const [showTokenSection, setShowTokenSection] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [clipboardNotification, setClipboardNotification] = useState('');
  const [engineerImageUrl, setEngineerImageUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [isEditingToken, setIsEditingToken] = useState(false);

  // Update page title when ticket is loaded
  useEffect(() => {
    if (id) {
      customersApiService
        .getTicketById(id)
        .then(async data => {
          // Fetch fresh customer data to avoid stale cached names
          let customerName = data.customer_name;
          try {
            const customer = await customersApiService.getCustomerByNumber(data.customer_number);
            if (customer && customer.customer_name) {
              customerName = customer.customer_name;
            }
          } catch (err) {
            console.warn('Could not fetch fresh customer data, using cached name:', err);
          }

          const pageTitle = `${data.ticket_number} - ${customerName} - ${data.issue_with}`;
          document.title = pageTitle;

          // Load saved token if exists, otherwise use default
          if (data.field_engineer_api_token) {
            setApiToken(data.field_engineer_api_token);
          }
        })
        .catch(err => {
          console.error('Error fetching ticket:', err);
          document.title = 'SSK - Support Swiss Knife';
        });
    } else {
      document.title = 'SSK - Support Swiss Knife';
    }

    return () => {
      document.title = 'SSK - Support Swiss Knife';
    };
  }, [id]);

  // Auto-fetch field engineers when component loads or token changes
  useEffect(() => {
    if (apiToken && id) {
      fetchFieldEngineers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Filter engineers based on search query
  const filteredEngineers = fieldEngineers.filter(engineer => {
    if (!searchQuery.trim()) return true;

    const query = searchQuery.toLowerCase();
    const firstName = (engineer.first_name || '').toLowerCase();
    const lastName = (engineer.last_name || '').toLowerCase();
    const nickname = (engineer.nickname || '').toLowerCase();
    const fullName = `${firstName} ${lastName}`.toLowerCase();

    return firstName.includes(query) ||
           lastName.includes(query) ||
           nickname.includes(query) ||
           fullName.includes(query);
  });

  // Reset index when search changes
  useEffect(() => {
    setCurrentEngineerIndex(0);
    setShowFullCard(false);
  }, [searchQuery]);

  // Update selected engineer when currentEngineerIndex changes
  useEffect(() => {
    if (filteredEngineers.length > 0 && currentEngineerIndex >= 0 && currentEngineerIndex < filteredEngineers.length) {
      const engineer = filteredEngineers[currentEngineerIndex];
      setSelectedEngineer(engineer);
      // Fetch engineer image if available
      if (engineer && (engineer.profile_picture || engineer.image || engineer.profile_image || engineer.photo)) {
        fetchEngineerImage(engineer.profile_picture || engineer.image || engineer.profile_image || engineer.photo);
      } else {
        setEngineerImageUrl(null);
      }
    } else {
      setSelectedEngineer(null);
      setEngineerImageUrl(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEngineerIndex, filteredEngineers]);

  // Cleanup image URL when component unmounts or image changes
  useEffect(() => {
    return () => {
      if (engineerImageUrl) {
        URL.revokeObjectURL(engineerImageUrl);
      }
    };
  }, [engineerImageUrl]);

  const handleUpdateToken = async () => {
    if (!apiToken || apiToken.trim() === '') {
      setError('Please enter an API token');
      return;
    }

    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      await fieldEngineerService.updateToken(id, apiToken);
      setSuccessMessage('API Token updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      setIsEditingToken(false);
      // Re-fetch field engineers with new token
      fetchFieldEngineers();
    } catch (err) {
      console.error('Error updating token:', err);
      setError(
        err.response?.data?.error || 'Failed to update API token. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchFieldEngineers = async () => {
    if (!apiToken) {
      setError('API token is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const data = await fieldEngineerService.getAllFieldEngineers(apiToken);
      setFieldEngineers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching field engineers:', err);
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          'Failed to fetch field engineers. Please check your API token.'
      );
      setFieldEngineers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchEngineerImage = async (imageFilename) => {
    if (!imageFilename || !apiToken) {
      setEngineerImageUrl(null);
      return;
    }

    setImageLoading(true);

    try {
      const imageBlob = await fieldEngineerService.getEngineerImage(imageFilename, apiToken);
      const imageUrl = URL.createObjectURL(imageBlob);
      setEngineerImageUrl(imageUrl);
    } catch (err) {
      console.error('Error fetching engineer image:', err);
      setEngineerImageUrl(null);
    } finally {
      setImageLoading(false);
    }
  };

  const handleNextEngineer = () => {
    if (currentEngineerIndex < filteredEngineers.length - 1) {
      setCurrentEngineerIndex(currentEngineerIndex + 1);
      setShowFullCard(false);
    }
  };

  const handlePreviousEngineer = () => {
    if (currentEngineerIndex > 0) {
      setCurrentEngineerIndex(currentEngineerIndex - 1);
      setShowFullCard(false);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const handleKeyPress = e => {
    if (e.key === 'Enter' && isEditingToken) {
      handleUpdateToken();
    }
  };

  const copyTokenToClipboard = () => {
    if (!apiToken) return;

    navigator.clipboard.writeText(apiToken).then(() => {
      setTokenCopyNotification('Token copied to clipboard!');
      setTimeout(() => setTokenCopyNotification(''), 3000);
    }).catch(err => {
      console.error('Failed to copy token:', err);
      setTokenCopyNotification('Failed to copy token');
      setTimeout(() => setTokenCopyNotification(''), 3000);
    });
  };

  const copyWhoToClipboard = () => {
    if (!selectedEngineer) return;

    const firstName = selectedEngineer.first_name || '';
    const lastName = selectedEngineer.last_name || '';
    const phone = selectedEngineer.phone || '';
    const email = selectedEngineer.email || '';

    const whoText = `Who: ${firstName} ${lastName}, ${phone}, ${email}`;

    navigator.clipboard.writeText(whoText).then(() => {
      setClipboardNotification('Copied to clipboard!');
      setTimeout(() => setClipboardNotification(''), 5000);
    }).catch(err => {
      console.error('Failed to copy:', err);
      setClipboardNotification('Failed to copy to clipboard');
      setTimeout(() => setClipboardNotification(''), 5000);
    });
  };

  return (
    <div className='min-h-screen p-6'>
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-800 mb-6">
          Field Engineer
        </h1>

        {/* API Token Section - Only visible for TopGun users */}
        {isTopGun && (
        <div className="bg-white rounded-lg shadow-md mb-6">
          {/* Collapsible Header */}
          <div
            onClick={() => setShowTokenSection(!showTokenSection)}
            className="flex items-center justify-between p-6 cursor-pointer hover:bg-gray-50 transition-colors rounded-t-lg"
          >
            <h2 className="text-xl font-semibold text-gray-700 flex items-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-6 h-6 text-orange-500"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.75 5.25a3 3 0 0 1 3 3m3 0a6 6 0 0 1-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1 1 21.75 8.25Z"
                />
              </svg>
              API Token Configuration
            </h2>
            <button
              type="button"
              className="p-2 hover:bg-gray-200 rounded-full transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className={`w-5 h-5 text-gray-600 transition-transform ${showTokenSection ? 'rotate-180' : ''}`}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m19.5 8.25-7.5 7.5-7.5-7.5"
                />
              </svg>
            </button>
          </div>

          {/* Collapsible Content */}
          {showTokenSection && (
            <div className="px-6 pb-6 border-t border-gray-200 pt-6">
              <div className="flex gap-3 items-start">
            <div className="flex-1">
              <label
                htmlFor="apiToken"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                API Token
              </label>
              <input
                type="text"
                id="apiToken"
                value={apiToken}
                onChange={e => setApiToken(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={!isEditingToken}
                placeholder="Enter your Field Engineer API token"
                className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-orange-400 focus:border-orange-400 font-mono text-sm ${
                  !isEditingToken
                    ? 'bg-gray-100 cursor-not-allowed'
                    : 'bg-white'
                }`}
              />
            </div>

            <div className="pt-7 flex gap-2">
              <button
                onClick={copyTokenToClipboard}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 flex items-center gap-1"
                title="Copy token to clipboard"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-5 h-5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184"
                  />
                </svg>
                Copy
              </button>

              {isEditingToken ? (
                <>
                  <button
                    onClick={handleUpdateToken}
                    disabled={loading}
                    className={`px-6 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-400 ${
                      loading ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {loading ? 'Updating...' : 'Update Token'}
                  </button>
                  <button
                    onClick={() => {
                      setIsEditingToken(false);
                      // Reset to saved token or default
                      setError('');
                    }}
                    className="px-6 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-400"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setIsEditingToken(true)}
                  className="px-6 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-400"
                >
                  Edit Token
                </button>
              )}
            </div>
          </div>

          {/* Token Copy Notification */}
          {tokenCopyNotification && (
            <div className="mt-4 p-3 bg-blue-100 border border-blue-400 text-blue-700 rounded-md">
              {tokenCopyNotification}
            </div>
          )}

          {/* Success/Error Messages */}
          {successMessage && (
            <div className="mt-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-md">
              {successMessage}
            </div>
          )}

          {error && (
            <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
              {error}
            </div>
          )}
            </div>
          )}
        </div>
        )}

        {/* Field Engineers Carousel Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-700">
              Field Engineers
            </h2>
            <button
              onClick={fetchFieldEngineers}
              disabled={loading}
              className={`px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-400 flex items-center gap-2 ${
                loading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
                />
              </svg>
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>

          {loading && fieldEngineers.length === 0 ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
              <p className="mt-4 text-gray-600">Loading field engineers...</p>
            </div>
          ) : fieldEngineers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No field engineers available
            </div>
          ) : (
            <div className="relative">
              {/* Search Box */}
              <div className="mb-4">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search by name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
                  />
                  {searchQuery && (
                    <button
                      onClick={handleClearSearch}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded transition-colors"
                      title="Clear search"
                      type="button"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                        stroke="currentColor"
                        className="w-5 h-5 text-gray-500"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6 18 18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                </div>
                {searchQuery && (
                  <p className="mt-2 text-sm text-gray-600">
                    Found {filteredEngineers.length} engineer{filteredEngineers.length !== 1 ? 's' : ''}
                  </p>
                )}
              </div>

              {filteredEngineers.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No engineers found matching "{searchQuery}"
                </div>
              ) : (
                <>
                  {/* Carousel Container */}
                  <div className="flex items-center gap-4">
                {/* Previous Button */}
                <button
                  onClick={handlePreviousEngineer}
                  disabled={currentEngineerIndex === 0}
                  className={`p-2 rounded-full bg-orange-500 text-white hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-400 ${
                    currentEngineerIndex === 0 ? 'opacity-30 cursor-not-allowed' : ''
                  }`}
                  title="Previous Engineer"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                    className="w-6 h-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15.75 19.5 8.25 12l7.5-7.5"
                    />
                  </svg>
                </button>

                {/* Engineer Card Preview */}
                {selectedEngineer && (
                  <div className="flex-1 bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg p-6 border-2 border-orange-200">
                    <div className="flex items-center gap-6">
                      {/* Profile Image */}
                      <div className="flex-shrink-0">
                        {engineerImageUrl ? (
                          <img
                            src={engineerImageUrl}
                            alt={`${selectedEngineer.first_name || ''} ${selectedEngineer.last_name || ''}`}
                            className="w-24 h-24 rounded-full object-cover border-4 border-orange-500 shadow-lg"
                          />
                        ) : imageLoading ? (
                          <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center">
                            <svg
                              className="animate-spin h-8 w-8 text-orange-500"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                          </div>
                        ) : (
                          <div className="w-24 h-24 rounded-full bg-orange-300 flex items-center justify-center text-white text-3xl font-bold">
                            {(selectedEngineer.first_name?.[0] || '') + (selectedEngineer.last_name?.[0] || '')}
                          </div>
                        )}
                      </div>

                      {/* Engineer Info */}
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-gray-800 mb-2">
                          {selectedEngineer.first_name || ''} {selectedEngineer.last_name || ''}
                        </h3>
                        <p className="text-gray-700 mb-1">
                          <span className="font-semibold">Phone:</span> {selectedEngineer.phone || 'N/A'}
                        </p>
                        <p className="text-gray-700">
                          <span className="font-semibold">Email:</span> {selectedEngineer.email || 'N/A'}
                        </p>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-col gap-2">
                        <button
                          onClick={() => setShowFullCard(!showFullCard)}
                          className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-400 flex items-center gap-2"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-5 h-5"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d={showFullCard
                                ? 'M4.5 15.75l7.5-7.5 7.5 7.5'
                                : 'M19.5 8.25l-7.5 7.5-7.5-7.5'
                              }
                            />
                          </svg>
                          {showFullCard ? 'Hide Details' : 'View Details'}
                        </button>
                        <button
                          onClick={copyWhoToClipboard}
                          className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 flex items-center gap-2"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-5 h-5"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184"
                            />
                          </svg>
                          Copy
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Next Button */}
                <button
                  onClick={handleNextEngineer}
                  disabled={currentEngineerIndex === filteredEngineers.length - 1}
                  className={`p-2 rounded-full bg-orange-500 text-white hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-400 ${
                    currentEngineerIndex === filteredEngineers.length - 1 ? 'opacity-30 cursor-not-allowed' : ''
                  }`}
                  title="Next Engineer"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                    className="w-6 h-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="m8.25 4.5 7.5 7.5-7.5 7.5"
                    />
                  </svg>
                </button>
              </div>

              {/* Carousel Counter - Moved to Bottom */}
              <div className="text-center mt-4 text-sm text-gray-600">
                Engineer {currentEngineerIndex + 1} of {filteredEngineers.length}
              </div>
            </>
          )}
            </div>
          )}
        </div>

        {/* Engineer Full Details Card - Only shown when expanded */}
        {selectedEngineer && showFullCard && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-700 mb-4">
              All Field Engineer Details
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(selectedEngineer).map(([key, value]) => (
                <div key={key} className="border-b pb-2">
                  <p className="text-sm font-medium text-gray-500 capitalize">
                    {key.replace(/_/g, ' ')}
                  </p>
                  <p className="text-base text-gray-800 mt-1">
                    {typeof value === 'object' && value !== null
                      ? JSON.stringify(value, null, 2)
                      : value?.toString() || 'N/A'}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Toast Notification for Clipboard */}
      {clipboardNotification && (
        <div className="fixed top-4 right-4 z-50 animate-fade-in">
          <div className="bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-5 h-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
              />
            </svg>
            {clipboardNotification}
          </div>
        </div>
      )}
    </div>
  );
}

export default FieldEngineer;
